import { CandlestickData, PriceFormat } from 'lightweight-charts'
import store, { RootState } from '../../../store'
import { green, red } from '../../../assets/colors'
import { calculateChange, toLocaleStringIn } from '../../../util/general'
import { extractColorFromDataPoint, formatIndicatorOutputValue } from '../../../util/chart'
import { LiveMarketTextSelector } from '../../common/LiveMarketText'
import { LiveTextSelector } from '../../common/LiveText'
import { getEffectiveChartSnapshotSeriesGroup, subscribeChartLiveData } from '../chartSnapshotStore'
import { SeriesData } from '../chart.typings'
import { selectChartSeriesGroups } from '../../../selectors/chart'
import { getChartBidAsk, subscribeChartBidAsk } from '../chartBidAskStore'

const getChartInstance = (viewKey: string) => window.chartManager?.getChart(viewKey)

const displaySelectorCache = new Map<string, LiveTextSelector>()
const colorSelectorCache = new Map<string, LiveTextSelector>()
const reduxDisplayCache = new Map<string, LiveMarketTextSelector>()
const reduxColorCache = new Map<string, LiveMarketTextSelector>()

const getCachedLiveTextSelector = (
    cache: Map<string, LiveTextSelector>,
    cacheKey: string,
    factory: () => LiveTextSelector
): LiveTextSelector => {
    if (!cache.has(cacheKey)) {
        cache.set(cacheKey, factory())
    }
    return cache.get(cacheKey)!
}

const getCachedReduxSelector = (
    cache: Map<string, LiveMarketTextSelector>,
    cacheKey: string,
    factory: () => LiveMarketTextSelector
): LiveMarketTextSelector => {
    if (!cache.has(cacheKey)) {
        cache.set(cacheKey, factory())
    }
    return cache.get(cacheKey)!
}

const formatDisplayValue = (
    value: number,
    priceFormat: PriceFormat['type'],
    precision?: number
): string => formatIndicatorOutputValue(value, priceFormat, precision)

const getPrimaryCandles = (viewKey: string): CandlestickData[] => {
    const primary = getEffectiveChartSnapshotSeriesGroup(viewKey, 'primary')
    if (!primary) return []
    const [, data] = Object.entries(primary)[0] ?? []
    return (data ?? []) as CandlestickData[]
}

const getSeriesCandles = (viewKey: string, seriesGroupId: string, seriesId: string): CandlestickData[] => {
    const group = getEffectiveChartSnapshotSeriesGroup(viewKey, seriesGroupId)
    return (group?.[seriesId] ?? []) as CandlestickData[]
}

const candleColor = (candle?: CandlestickData): string => {
    if (!candle) return 'inherit'
    return candle.close >= candle.open ? green : red
}

const formatSigned = (value: number, decimals = 2): string => {
    const abs = Math.abs(value) || 0
    const sign = value > 0 ? '+' : value < 0 ? '-' : ''
    return `${sign}${abs.toFixed(decimals)}`
}

export const buildSeriesMetadataLiveSubscribe = (viewKey: string, seriesGroupIds: string[]) => {
    return (listener: () => void) => subscribeChartLiveData(viewKey, seriesGroupIds, listener)
}

export const buildChartBidAskSubscribe = (viewKey: string) => {
    return (listener: () => void) => subscribeChartBidAsk(viewKey, listener)
}

export const getChartTopBidDisplaySelector = (viewKey: string, priceMultiplier: number): LiveTextSelector =>
    getCachedLiveTextSelector(displaySelectorCache, `chartTopBid:${viewKey}:${priceMultiplier}`, () => () => {
        const { topBid } = getChartBidAsk(viewKey)
        return toLocaleStringIn(topBid / priceMultiplier)
    })

export const getChartTopAskDisplaySelector = (viewKey: string, priceMultiplier: number): LiveTextSelector =>
    getCachedLiveTextSelector(displaySelectorCache, `chartTopAsk:${viewKey}:${priceMultiplier}`, () => () => {
        const { topAsk } = getChartBidAsk(viewKey)
        return toLocaleStringIn(topAsk / priceMultiplier)
    })

export const getChartBidAskSpreadSelector = (viewKey: string, priceMultiplier: number): LiveTextSelector =>
    getCachedLiveTextSelector(displaySelectorCache, `chartSpread:${viewKey}:${priceMultiplier}`, () => () => {
        const { topBid, topAsk } = getChartBidAsk(viewKey)
        return toLocaleStringIn(Math.abs(topAsk - topBid) / priceMultiplier)
    })

export const getIndicatorCustomLabelSelector = (
    viewKey: string,
    seriesGroupId: string,
    seriesId: string
): LiveTextSelector =>
    getCachedLiveTextSelector(
        displaySelectorCache,
        `indicatorLabel:${viewKey}:${seriesGroupId}:${seriesId}`,
        () => () => {
            const groupSnap = getEffectiveChartSnapshotSeriesGroup(viewKey, seriesGroupId)
            const data = groupSnap?.[seriesId]
            const lastCandle = data?.[(data?.length ?? 0) - 1] as { customValues?: Record<string, unknown> } | undefined
            return (lastCandle?.customValues?.label as string | undefined) ?? ''
        }
    )

export const getStrikeDisplaySelector = (viewKey: string, priceMultiplier: number): LiveTextSelector =>
    getCachedLiveTextSelector(displaySelectorCache, `strike:${viewKey}:${priceMultiplier}`, () => () => {
        const primarySnap = getEffectiveChartSnapshotSeriesGroup(viewKey, 'primary')
        if (!primarySnap) return '-'
        const [, data] = Object.entries(primarySnap)[0] ?? []
        const lastCandle = data?.[(data?.length ?? 0) - 1] as { referencedSecurityCode?: string } | undefined
        const refCode = lastCandle?.referencedSecurityCode ?? ''
        const chart = getChartInstance(viewKey)
        const strikePrice = chart?.dataManager?.referenceSecurities?.[refCode]?.strikePrice
        return strikePrice != null ? toLocaleStringIn(strikePrice / priceMultiplier) : '-'
    })

export const getPrimaryOhlcColorSelector = (viewKey: string): LiveTextSelector =>
    getCachedLiveTextSelector(colorSelectorCache, `primaryOhlcColor:${viewKey}`, () => () => {
        const candles = getPrimaryCandles(viewKey)
        return candleColor(candles[candles.length - 1])
    })

export const getPrimaryOpenSelector = (viewKey: string): LiveTextSelector =>
    getCachedLiveTextSelector(displaySelectorCache, `primaryOpen:${viewKey}`, () => () => {
        const candles = getPrimaryCandles(viewKey)
        return toLocaleStringIn(candles[candles.length - 1]?.open || 0)
    })

export const getPrimaryHighSelector = (viewKey: string): LiveTextSelector =>
    getCachedLiveTextSelector(displaySelectorCache, `primaryHigh:${viewKey}`, () => () => {
        const candles = getPrimaryCandles(viewKey)
        return toLocaleStringIn(candles[candles.length - 1]?.high || 0)
    })

export const getPrimaryLowSelector = (viewKey: string): LiveTextSelector =>
    getCachedLiveTextSelector(displaySelectorCache, `primaryLow:${viewKey}`, () => () => {
        const candles = getPrimaryCandles(viewKey)
        return toLocaleStringIn(candles[candles.length - 1]?.low || 0)
    })

export const getPrimaryCloseSelector = (viewKey: string): LiveTextSelector =>
    getCachedLiveTextSelector(displaySelectorCache, `primaryClose:${viewKey}`, () => () => {
        const candles = getPrimaryCandles(viewKey)
        return toLocaleStringIn(candles[candles.length - 1]?.close || 0)
    })

export const getPrimaryBarChangeSelector = (viewKey: string): LiveTextSelector =>
    getCachedLiveTextSelector(displaySelectorCache, `primaryBarChange:${viewKey}`, () => () => {
        const candles = getPrimaryCandles(viewKey)
        if (candles.length < 2) return '0.00 (0.00%)'
        const change = (candles[1]?.close ?? 0) - (candles[0]?.close ?? 0)
        const changePercent = (change * 100) / (candles[0]?.close ?? 1)
        return `${formatSigned(change)} (${formatSigned(changePercent)}%)`
    })

export const getDayChangeReduxSelectors = (
    chartSecurityKey: string,
    expressionMode: boolean
): { text: LiveMarketTextSelector; color: LiveMarketTextSelector } => {
    const prefix = `seriesMetaDayChg:${chartSecurityKey}:${expressionMode}`
    const text = getCachedReduxSelector(reduxDisplayCache, prefix, () => (state: RootState) => {
        const snap = state.securityStore.snapshots[chartSecurityKey]
        if (!snap) return ''
        const { change, changePercentage } = calculateChange(snap.lastTradedPrice, snap.previousClose)
        const changeVal = expressionMode ? change : change / 100
        return `${change > 0 ? '+' : ''}${changeVal.toFixed(2)} (${changePercentage > 0 ? '+' : ''}${changePercentage.toFixed(2)}%)`
    })
    const color = getCachedReduxSelector(reduxColorCache, `${prefix}:color`, () => (state: RootState) => {
        const snap = state.securityStore.snapshots[chartSecurityKey]
        if (!snap) return 'inherit'
        const { change } = calculateChange(snap.lastTradedPrice, snap.previousClose)
        if (change > 0) return green
        if (change < 0) return red
        return 'inherit'
    })
    return { text, color }
}

export const getEmbeddedDayChangeReduxSelectors = (
    viewKey: string,
    chartSecurityKey: string,
    expressionMode: boolean
): {
    ltpText: LiveTextSelector
    ltpColor: LiveTextSelector
    changeText: LiveMarketTextSelector
    changeColor: LiveMarketTextSelector
} => {
    const ltpText = getCachedLiveTextSelector(displaySelectorCache, `embedLtp:${viewKey}`, () => () => {
        const candles = getPrimaryCandles(viewKey)
        return toLocaleStringIn(candles[candles.length - 1]?.close || 0)
    })
    const ltpColor = getCachedLiveTextSelector(colorSelectorCache, `embedLtpColor:${viewKey}`, () => () => {
        const candles = getPrimaryCandles(viewKey)
        const candleCol = candleColor(candles[candles.length - 1])
        if (!chartSecurityKey) return candleCol
        const snap = store.getState().securityStore.snapshots[chartSecurityKey]
        if (!snap) return candleCol
        const { change } = calculateChange(snap.lastTradedPrice, snap.previousClose)
        if (change > 0) return green
        if (change < 0) return red
        return candleCol
    })

    const prefix = `seriesMetaEmbedChg:${chartSecurityKey}:${expressionMode}`
    const changeText = getCachedReduxSelector(reduxDisplayCache, prefix, () => (state: RootState) => {
        const snap = state.securityStore.snapshots[chartSecurityKey]
        if (!snap) return ''
        const { change, changePercentage } = calculateChange(snap.lastTradedPrice, snap.previousClose)
        const changeVal = expressionMode ? change : change / 100
        const sign = change > 0 ? '+' : change < 0 ? '-' : ''
        const pctSign = changePercentage > 0 ? '+' : changePercentage < 0 ? '-' : ''
        return `${sign}${Math.abs(changeVal).toFixed(2)} (${pctSign}${Math.abs(changePercentage).toFixed(2)}%)`
    })
    const changeColor = getCachedReduxSelector(reduxColorCache, `${prefix}:color`, () => (state: RootState) => {
        const snap = state.securityStore.snapshots[chartSecurityKey]
        if (!snap) return 'inherit'
        const { change } = calculateChange(snap.lastTradedPrice, snap.previousClose)
        if (change > 0) return green
        if (change < 0) return red
        return 'inherit'
    })

    return { ltpText, ltpColor, changeText, changeColor }
}

export const getComparableCloseSelector = (
    viewKey: string,
    seriesGroupId: string,
    seriesId: string,
    priceFormat: PriceFormat['type']
): LiveTextSelector =>
    getCachedLiveTextSelector(
        displaySelectorCache,
        `comparableClose:${viewKey}:${seriesGroupId}:${seriesId}:${priceFormat}`,
        () => () => {
            const candles = getSeriesCandles(viewKey, seriesGroupId, seriesId)
            const last = candles[candles.length - 1]
            return formatDisplayValue(last?.close || 0, priceFormat)
        }
    )

export const getIndicatorValueSelector = (
    viewKey: string,
    seriesGroupId: string,
    seriesId: string,
    priceFormat: PriceFormat['type'],
    chartType: string,
    staticColor?: string
): { text: LiveTextSelector; color: LiveTextSelector } => {
    const cacheKey = `indicator:${viewKey}:${seriesGroupId}:${seriesId}:${priceFormat}:${chartType}:${staticColor ?? ''}`
    const text = getCachedLiveTextSelector(displaySelectorCache, `${cacheKey}:text`, () => () => {
        const outputValuesPrecision = selectChartSeriesGroups(viewKey)(store.getState())?.[seriesGroupId]
            ?.outputValuesPrecision
        const candles = getSeriesCandles(viewKey, seriesGroupId, seriesId)
        const last = candles[candles.length - 1] as SeriesData & { value?: number; label?: string }
        let display = formatDisplayValue(last?.value || 0, priceFormat, outputValuesPrecision)
        if (last?.label) display += ` ${last.label}`
        return display
    })
    const color = getCachedLiveTextSelector(colorSelectorCache, `${cacheKey}:color`, () => () => {
        if (staticColor) return staticColor
        const candles = getSeriesCandles(viewKey, seriesGroupId, seriesId)
        const last = candles[candles.length - 1]
        return extractColorFromDataPoint(last, chartType as any) ?? 'inherit'
    })
    return { text, color }
}

export const getVolumeIndicatorColorSelector = (viewKey: string): LiveTextSelector =>
    getCachedLiveTextSelector(colorSelectorCache, `volumeColor:${viewKey}`, () => () => {
        const primary = getPrimaryCandles(viewKey)
        const last = primary[primary.length - 1]
        return last && last.close > last.open ? green : red
    })
